import type { GeneralSurveyCard } from '../ui/general-survey-card';
import type { SpecialistSurveyCard } from '../ui/specialist-survey-card';

import { ComponentProps } from 'react';

import { SurveyType } from '@graphql/requests';

export const surveyData: {
  generalSurveys: Array<ComponentProps<typeof GeneralSurveyCard>>;
  specialistSurveys: Array<
    ComponentProps<typeof SpecialistSurveyCard> & {
      type: SurveyType;
    }
  >;
} = {
  generalSurveys: [
    {
      level: 1,
      name: 'Basic',
      type: null,
      summary: `A basic report of visible defects and urgent issues.`,
      explanation: `Level 1 surveys are not detailed enough to provide you with any meaningful information to help you uncover serious faults or issues with the property.`,
      includedItems: ['overall'],
      isDisabled: true,
    },
    {
      level: 2,
      name: `Standard`,
      type: SurveyType.Level2,
      summary: `A more detailed report that identifies structural issues, estimates the potential cost of repairs for urgent defects and describes the overall condition of the property.`,
      includedItems: [
        'overall',
        'roof',
        'walls',
        'windows',
        'doors',
        'structural',
        'visible-defects',
        'recommendation',
      ],
      explanation: `Level 2 surveys are suitable for most conventional properties, as long as they have not been modified or are over 50 years old.`,
    },
    {
      level: 3,
      name: `Detailed`,
      type: SurveyType.Level3,
      summary: `Level 3 surveys are much more detailed reports on the structural integrity and specialist defects that may occur with very large, unusual or old properties.`,
      includedItems: [
        'overall',
        'roof',
        'walls',
        'windows',
        'doors',
        'structural',
        'visible-defects',
        'recommendation',
        'invisible-defects',
        'secondary-signs-damp',
        'materials',
        'repair-timeline',
        'cost-estimation',
        'energy-efficiency',
      ],
      explanation: `Most people believe they need a level 3 survey to make sure they have as much information as possible about a property, but a level 3 survey is often inappropriate for a property unless it's very big, old or unusual in its construction (think 8 bedroom, timber-framed, thatched home). A level 2 survey is often the most appropriate.`,
    },
  ],
  specialistSurveys: [
    {
      name: 'Asbestos',
      type: SurveyType.Asbestos,
      price: null,
      description: `A report on the extent and condition of any asbestos identified by the seller or the buyer's survey.`,
      explanation: `Usually asbestos reports aren't required, but if you know that asbestos is present it's a good idea to get a qualified professional to assess it for you to keep you and your family safe. This is especially important before any alterations, such as demolitions or extensions.`,
      imageUrl: '/images/survey/survey-asbestos.png',
    },
    {
      name: 'CCTV Drain',
      type: SurveyType.CctvDrain,
      price: 604,
      description: `This is a detailed inspection using high-tech camera technology that identifies any issues with drainage.`,
      explanation: `It might seem unnecessary, but finding out if there are issues with drainage before you purchase the property allows you to negotiate repairs with the seller before ownership is transferred, or re-negotiate the property price to accommodate for repairs. It's better to find out if there's a problem before you're responsible as the owner of the property.`,
      imageUrl: '/images/survey/survey-drain.png',
    },
    {
      name: 'Damp & Timber',
      type: SurveyType.DampAndTimber,
      price: 580,
      description: `This is a detailed inspection of the interior and exterior of the property if excessive damp, woodworm or related issues are suspected. Usually your surveyor or your mortgage lender's surveyor will flag the need for this kind of survey.`,
      explanation: `Unless there are obvious signs of damp or timber-related issues, or a qualified surveyor has suggested the need for this type of report, your level 2 survey should cover most properties' exposure to damp.`,
      imageUrl: '/images/survey/survey-damp.png',
    },
    {
      name: 'Electrical',
      type: SurveyType.Electrical,
      price: 520,
      description: `The Electrical Installation Condition Report is performed by a qualified electrician to tell you the quality of the electrical infrastructure in a property. This is not legally required, unless you are planning to let out the property. Since April 2021 it is a legal requirement to get an EICR certificate before letting the property.`,
      explanation: `Getting an EICR is a great way to give yourself peace of mind when buying a property. Electrical works can be expensive and invasive, so ensuring anything crucial is fixed before you are the legal owner of the property can save you money and inconvenience. New builds will always come with an EICR as a condition of sale, so don't worry about this one if you're moving into a new property.`,
      imageUrl: '/images/survey/survey-electrics.png',
    },
    {
      name: 'Gas',
      type: SurveyType.Gas,
      price: 448,
      description: `If your surveyor notices there is no recent service history on your gas or boiler, or there are visual signs of degradation or ageing, it is likely they’ll encourage you to get a Gas report done. This is a detailed inspection by a Gas Safe registered engineer who will ensure the gas systems and appliances in your home are safe to use.`,
      explanation: ``,
      imageUrl: '/images/survey/survey-gas.png',
    },
    {
      name: 'Home Services',
      type: SurveyType.HomeServices,
      price: 580,
      description: `The Home Services Report will give you an in-depth understanding of the quality of the plumbing, gas and heating infrastructure in a property. Typically, there is a full inspection of the boiler, a 16 point inspection of the plumbing and a 16 point inspection of the heating along with photographs.`,
      explanation: `Getting an HSR is important for run-down or older properties, or for properties where a recent HSR is unavailable. The cost of repairing plumbing, heating and gas infrastructure can be extensive and invasive, so it's a good idea to understand the condition of the property before purchasing it.`,
      imageUrl: '/images/survey/survey-home-services.png',
    },
    {
      name: 'Japanese Knotweed',
      type: SurveyType.JapaneseKnotweed,
      price: null,
      description: `Japanese knotweed is an invasive plant species, which can completely infest gardens in the UK. If you suspect your property has Japanese Knotweed, it is a good idea to ask a specialist to assess and report their findings to you.`,
      explanation: `Japanese Knotweed is increasingly rare, but can cause significant trouble if not taken care of. For example, some mortgage lenders will not lend if there is Japanese Knotweed found at the property or within 3 metres of the property's boundary.`,
      imageUrl: '/images/survey/survey-knotweed.png',
    },
    {
      name: 'Roof',
      type: SurveyType.Roof,
      price: 580,
      description: `A roof report is a detailed investigation into the condition, structural integrity and expected lifespan of a roof. This is essential if roof repairs or amendments are planned on the property.`,
      explanation: `Not everybody needs a roof report. Your surveyor will likely indicate whether a roof report is necessary following your Level 2/3 survey.`,
      imageUrl: '/images/survey/survey-roof.png',
    },
    {
      name: 'Structural Engineers',
      type: SurveyType.StructuralEngineers,
      price: 940,
      description: `This is a thorough investigation into the presence, causes and potential costs of structural defects in a property. Usually the need for a structural survey will be identified after your level 2 survey.`,
      explanation: `Structural surveys are usually not needed if there are no signs that there are any structural defects present on a property. Your surveyor should indicate whether there is a need for this kind of report, and will usually only do so if they suspect serious defects. Although expensive, the results of the survey could allow you to renegotiate the price of the property, if serious defects are found.`,
      imageUrl: '/images/survey/survey-structural.png',
    },
    {
      name: 'Tree',
      type: SurveyType.Tree,
      price: 676,
      description: `If there are large trees on or near the property, it is likely you will need a Tree Survey to find out whether there is the possibility of damage to the property by the tree's roots, or if it were to fall.`,
      explanation: `Tree surveys are usually unnecessary, unless there are trees on or very close to the property. Similarly to other surveys, it is likely that your mortgage lender would require a tree survey in order to lend, if trees are present.`,
      imageUrl: '/images/survey/survey-tree.png',
    },
    {
      name: 'Wall Tie',
      type: SurveyType.WallTie,
      price: 640,
      description: `Wall ties link the two 'leaves' of a cavity wall. In properties with cavity walls that are found to have cracks, bowing or other visual defects in the brick work, it is important to assess whether the damage is superficial or if the wall ties are damaged. Damaged wall ties can threaten the structural integrity of the wall.`,
      explanation: `A wall tie report is essential if the property's cavity walls are flagged as issues on your level 2/3 survey. The report will tell you how bad the damage is and help you calculate the costs of repairs, which you can then use to renegotiate the price of the property with the seller.`,
      imageUrl: '/images/survey/survey-wall-ties.png',
    },
  ],
};
